import React, { useEffect, useRef, useState, useMemo } from "react";
import { X, Plus, Image as ImageIcon, Loader2, Eye, Video,  Search, ChevronLeft, ChevronRight } from "lucide-react";
import { GBP } from "../../../types/Listing";
import { useLocationMedia } from "../../../hooks/Uselocationmedia";
import ReactDOM from "react-dom";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  gbp: GBP | null;
  onAdd: () => void;
  subjectEmail: string;
}

type FilterType = "ALL" | "PHOTO" | "VIDEO";
const ITEMS_PER_PAGE = 11; 

const PhotosModal: React.FC<Props> = ({ isOpen, onClose, gbp, onAdd,subjectEmail }) => {
  const { data: media = [], isLoading } = useLocationMedia(gbp, isOpen,subjectEmail);
  const modalRef = useRef<HTMLDivElement>(null);
  
  const [activeFilter, setActiveFilter] = useState<FilterType>("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, onClose]);

  const filteredMedia = useMemo(() => {
    return media.filter(item => {
      const matchesType = activeFilter === "ALL" || item.mediaFormat === activeFilter;
      const matchesSearch = (item.name || "").toLowerCase().includes(searchTerm.toLowerCase());
      return matchesType && matchesSearch;
    });
  }, [media, activeFilter, searchTerm]);

  const totalPages = Math.ceil(filteredMedia.length / ITEMS_PER_PAGE);
  const paginatedMedia = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredMedia.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredMedia, currentPage]);

  useEffect(() => { setCurrentPage(1); }, [activeFilter, searchTerm]);

  if (!isOpen || !gbp) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center px-4 bg-slate-900/70 backdrop-blur-md">
      <div 
        ref={modalRef}
        className="flex flex-col bg-slate-50 w-full rounded-2xl max-w-5xl shadow-2xl animate-[gbpFadeIn_0.2s_ease] max-h-[85vh] overflow-hidden"
      >
         <div className="flex flex-col px-6 py-4 bg-white border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-600 text-white">
                <ImageIcon size={18} />
              </div>
              <div>
                <h2 className="m-0 text-[15px] font-black text-slate-900 tracking-tight">Médiathèque</h2>
                <p className="m-0 text-[10px] font-bold text-slate-400 uppercase tracking-widest">{gbp.name}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 transition-all"><X size={16} /></button>
          </div>

          <div className="flex gap-3 items-center justify-between bg-slate-50 p-1.5 rounded-xl border border-slate-100">
            <div className="flex gap-1">
              {(["ALL", "PHOTO", "VIDEO"] as FilterType[]).map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveFilter(t)}
                  className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${
                    activeFilter === t ? "bg-white text-blue-600 shadow-sm ring-1 ring-slate-200" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t === "ALL" ? "Tout" : t === "PHOTO" ? "Photos" : "Vidéos"}
                </button>
              ))}
            </div>
            <div className="relative flex-1 max-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
              <input 
                type="text" 
                placeholder="Rechercher..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 rounded-lg border-none bg-white text-xs outline-none shadow-sm focus:ring-1 focus:ring-blue-200"
              />
            </div>
          </div>
        </div>

         <div className="flex-1 p-6 overflow-y-auto"> 
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Loader2 className="animate-spin text-blue-500 mb-2" size={24} />
                <span className="text-[10px] font-bold uppercase tracking-widest">Chargement...</span>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {currentPage === 1 && (
                <button 
                    onClick={onAdd} 
                    className="group aspect-square rounded-2xl border-2 border-dashed border-slate-200 bg-white flex flex-col items-center justify-center gap-2 hover:border-blue-400 hover:bg-blue-50/50 transition-all"
                >
                    <div className="p-3 rounded-xl bg-slate-50 text-slate-400 group-hover:bg-blue-100 group-hover:text-blue-600 transition-all">
                        <Plus size={20} />
                    </div>
                    <span className="text-[10px] font-black text-slate-400 uppercase">Ajouter</span>
                </button>
              )}

              {paginatedMedia.map((item, i) => (
                <div key={item.name ?? i} className="group relative aspect-square rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm hover:border-blue-300 transition-all duration-300">
                  {item.mediaFormat === 'VIDEO' ? (
                    <div className="w-full h-full flex items-center justify-center bg-slate-900 text-white">
                        <Video size={28} className="opacity-40" />
                    </div>
                  ) : (
                    <img src={item.thumbnailUrl ?? item.googleUrl} alt="" className="w-full h-full object-cover" />
                  )}
                  
                  <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                    <a href={item.googleUrl} target="_blank" rel="noreferrer" className="p-2.5 bg-white rounded-xl text-slate-900 hover:scale-110 transition-all shadow-xl">
                        <Eye size={18} />
                    </a>
                  </div>
                  
                  <div className="absolute top-2 left-2">
                    <div className="px-1.5 py-0.5 rounded-md bg-black/40 backdrop-blur-md text-white text-[8px] font-black uppercase">
                        {item.mediaFormat ?? 'IMAGE'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

         <div className="px-6 py-3 bg-white border-t border-slate-100 flex items-center justify-between flex-shrink-0">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
               {filteredMedia.length} fichiers • Page {currentPage}/{totalPages || 1}
          </span>
          
          <div className="flex gap-2">
              <button 
                disabled={currentPage === 1} 
                onClick={() => setCurrentPage(p => p - 1)} 
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-50 transition-all"
              >
                <ChevronLeft size={14} />
              </button>
              <button 
                disabled={currentPage === totalPages} 
                onClick={() => setCurrentPage(p => p + 1)} 
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-50 transition-all"
              >
                <ChevronRight size={14} />
              </button>
          </div>
        </div>
      </div>
    </div>,   
    document.body
  );
};

export default PhotosModal;